import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, ChevronsUpDown, Copy, Plus } from 'lucide-react';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../../components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import type {
  AdditionalAd,
  AttachDestination,
  AttachMediaMode,
  CreativeAsset,
  MetaAdDraftInput,
  MetaCallToAction,
  UtmParams,
} from '../../../types/metaAdDraft';
import type {
  CreateLeadFormInput,
  LeadFormSummary,
  PixelSummary,
} from '../../../types/metaAdResources';
import { emptyCreateLeadFormInput, leadFormDetailToCreateInput } from '../../../lib/leadForms';
import { getLeadForm, getLeadForms, getPixels } from '../api';
import { LeadFormModal } from '../components/LeadFormModal';
import { CTA_OPTIONS } from '../ctaOptions';
import { assembleUtmUrl } from '../utm';
import { AssetUploader } from '../components/AssetUploader';
import { PublishStatusToggle } from '../components/PublishStatusToggle';
import { AdditionalAdCard, type SharedAdCopy } from '../components/AdditionalAdCard';
import { RepeatableCopyField } from '../components/RepeatableCopyField';
import { VideoThumbnailField } from '../components/VideoThumbnailField';
import { useAutoThumbnail } from '../useAutoThumbnail';
import type { UploadedImage } from '../useAssetUpload';

const isHttpsUrl = (u: string): boolean => /^https:\/\/[^\s.]+\.[^\s]+/.test(u);

export function AdSection({
  value,
  onChange,
  attachMode,
}: {
  value: MetaAdDraftInput;
  onChange: (patch: Partial<MetaAdDraftInput>) => void;
  // "Add an ad to a live ad set": the ad is attached to an existing ad set, so the
  // ad set decides the destination, and Dynamic Creative / extra ads / pixel do not apply.
  attachMode?: { destination: AttachDestination; mediaMode?: AttachMediaMode };
}) {
  const accountId = value.accountId;
  // Part A: 'original' hides the uploaders (the source media stands in); 'exact' also hides copy + destination.
  const reuseMedia = attachMode?.mediaMode === 'original' || attachMode?.mediaMode === 'exact';
  const exactReuse = attachMode?.mediaMode === 'exact';
  const qc = useQueryClient();
  // Multi-ad-set: `ads` is the SHARED creative applied to every ad set.
  const ad = value.ads;
  const dest = ad.destination;
  const creative = ad.creative;
  const [formOpen, setFormOpen] = useState(false);
  // Lead Forms inline create + duplicate.
  const [dupOpen, setDupOpen] = useState(false);
  const [leadFormModalOpen, setLeadFormModalOpen] = useState(false);
  const [leadFormModalMode, setLeadFormModalMode] = useState<'create' | 'duplicate'>('create');
  const [leadFormModalInitial, setLeadFormModalInitial] = useState<CreateLeadFormInput>(
    emptyCreateLeadFormInput(),
  );
  const [pixelOpen, setPixelOpen] = useState(false);
  const [storyOpen, setStoryOpen] = useState(Boolean(ad.creative.storyAsset.url));
  const [confirmStoryRemove, setConfirmStoryRemove] = useState(false);
  const [pendingAssetType, setPendingAssetType] = useState<'image' | 'video' | null>(null);
  const [pendingDcoOff, setPendingDcoOff] = useState(false);
  const [confirmAddWithDco, setConfirmAddWithDco] = useState(false);
  const [confirmRemoveAdsForDco, setConfirmRemoveAdsForDco] = useState(false);

  const accountReady = accountId.trim().length > 0;

  // The Meta connection summary (Page / Ad Account / Instagram) lives in ONE place
  // — AdSetSection's "Meta Account Details" — so AdSection no longer renders a
  // Page/IG context block here. The IG delivery identity (ad.instagramAccountId)
  // is seeded from the account default on account-pick (igDefaultPatch) and flows
  // to publish unchanged.

  const leadForms = useQuery({
    queryKey: ['metaAdLeadForms', accountId],
    queryFn: () => getLeadForms(accountId).then((r) => r.leadForms),
    enabled: accountReady && dest.type === 'instant_form',
  });
  const selectedForm =
    dest.type === 'instant_form'
      ? leadForms.data?.find((f) => f.id === dest.leadGenFormId) ?? null
      : null;

  const pixels = useQuery({
    queryKey: ['metaAdPixels', accountId],
    queryFn: () => getPixels(accountId).then((r) => r.pixels),
    enabled: accountReady,
  });
  const selectedPixel = pixels.data?.find((p) => p.id === ad.pixelId) ?? null;

  const setUtm = (patch: Partial<UtmParams>) =>
    onChange({ ads: { ...ad, utmParams: { ...ad.utmParams, ...patch } } });

  // The base URL the UTM preview decorates: the website URL, or (Lead Gen) the
  // selected form's follow-up URL if it has one.
  const utmBaseUrl = dest.type === 'website' ? dest.url : selectedForm?.followUpActionUrl ?? '';
  const utmPreview = assembleUtmUrl(utmBaseUrl, ad.utmParams);

  const assetType = creative.assetType;

  // Phase 1 — the Story upload collapses behind an inline "+ Add Story-Specific
  // Creative" button next to the Feed upload. Auto-expanded when a Story asset is
  // already populated (existing drafts). Removing a populated asset confirms
  // first; collapsing an empty section doesn't.
  const showStory = storyOpen || Boolean(creative.storyAsset.url);
  function requestStoryRemove() {
    if (creative.storyAsset.url) setConfirmStoryRemove(true);
    else setStoryOpen(false);
  }
  function applyStoryRemove() {
    setAsset('storyAsset', null);
    setStoryOpen(false);
    setConfirmStoryRemove(false);
  }

  // Phase 2 — additional ads (sequential attach). Mutually exclusive with the
  // Dynamic Creative toggle (Meta: a DCO ad set takes exactly one ad) — both
  // directions are modal-guarded below.
  const additionalAds = ad.additionalAds ?? [];
  const anyAdPublished = additionalAds.some((a) => a.publishState?.status === 'published');
  const isLeadGen = value.campaign.objective === 'LEADS' && value.campaign.destinationType === 'instant_form';
  const EMPTY_SLOT: CreativeAsset = { type: null, url: null, path: null, thumbnailUrl: null, thumbnailPath: null };
  const makeAdditionalAd = (n: number): AdditionalAd => ({
    id: crypto.randomUUID(),
    name: `Ad ${n}`,
    assetType: 'image', // Phase 2a — per-ad; the card's selector switches it
    feedAsset: { ...EMPTY_SLOT },
    storyAsset: { ...EMPTY_SLOT },
    copyOverride: null,
    status: 'ACTIVE', // per-ad Active/Off, default ACTIVE
    publishState: null,
  });
  const setAdditionalAds = (next: AdditionalAd[]) => onChange({ ads: { ...ad, additionalAds: next } });
  function requestAddAd() {
    if (creative.useDynamicCreative) setConfirmAddWithDco(true);
    else setAdditionalAds([...additionalAds, makeAdditionalAd(additionalAds.length + 2)]);
  }
  // DCO was on → switching to standard ads trims the extra text variations AND
  // appends the first additional ad, in one atomic patch.
  function applyAddWithDco() {
    onChange({
      ads: {
        ...ad,
        creative: {
          ...creative,
          useDynamicCreative: false,
          headlines: [creative.headlines[0] ?? ''],
          primaryTexts: [creative.primaryTexts[0] ?? ''],
        },
        additionalAds: [...additionalAds, makeAdditionalAd(additionalAds.length + 2)],
      },
    });
    setConfirmAddWithDco(false);
  }
  // Enabling DCO while additional ads exist → confirm removing them (blocked
  // entirely once any is published — it's live on Meta).
  function applyRemoveAdsForDco() {
    onChange({
      ads: { ...ad, creative: { ...creative, useDynamicCreative: true }, additionalAds: [] },
    });
    setConfirmRemoveAdsForDco(false);
  }

  // The shared copy additional ads inherit (placeholder display in the cards).
  const sharedCopy: SharedAdCopy = {
    headline: creative.headlines.find((h) => h.trim() !== '') ?? '',
    primaryText: creative.primaryTexts.find((p) => p.trim() !== '') ?? '',
    description: creative.descriptions.find((d) => d.trim() !== '') ?? '',
    callToAction: creative.callToAction,
    linkUrl: dest.type === 'website' ? dest.url : '',
  };

  // Phase 1 — Dynamic Creative toggle. ON unlocks the multi-text fields; OFF is a
  // standard single-text ad. Switching OFF with extra variations confirms first
  // (they're trimmed to the first item). With additional ads present the toggle is
  // unavailable (tooltip) — clicking it offers to remove them instead.
  const dco = creative.useDynamicCreative;
  const dcoBlocked = additionalAds.length > 0;
  function requestDcoToggle() {
    if (dcoBlocked) {
      if (!anyAdPublished) setConfirmRemoveAdsForDco(true);
      return;
    }
    if (!dco) {
      setCreative({ useDynamicCreative: true });
    } else if (creative.headlines.length > 1 || creative.primaryTexts.length > 1) {
      setPendingDcoOff(true);
    } else {
      setCreative({ useDynamicCreative: false });
    }
  }
  function applyDcoOff() {
    setCreative({
      useDynamicCreative: false,
      headlines: [creative.headlines[0] ?? ''],
      primaryTexts: [creative.primaryTexts[0] ?? ''],
    });
    setPendingDcoOff(false);
  }

  function pickPixel(p: PixelSummary) {
    onChange({ ads: { ...ad, pixelId: p.id } });
    setPixelOpen(false);
  }

  const setCreative = (patch: Partial<typeof creative>) =>
    onChange({ ads: { ...ad, creative: { ...creative, ...patch } } });

  // D3.4 — adapt a CreativeAsset slot to/from the uploader's {imageUrl, imagePath}.
  const slotToUploaded = (slot: 'feedAsset' | 'storyAsset'): UploadedImage | null => {
    const a = creative[slot];
    return a.url && a.path ? { imageUrl: a.url, imagePath: a.path } : null;
  };
  // Set/clear an asset's FILE (preserves the slot type; clearing empties the slot).
  const setAsset = (slot: 'feedAsset' | 'storyAsset', next: UploadedImage | null) =>
    setCreative({
      [slot]: next
        ? { ...creative[slot], type: assetType, url: next.imageUrl, path: next.imagePath }
        : { type: null, url: null, path: null, thumbnailUrl: null, thumbnailPath: null },
    } as Partial<typeof creative>);
  // Set/clear an asset's optional custom thumbnail (video only).
  const setAssetThumb = (slot: 'feedAsset' | 'storyAsset', next: UploadedImage | null) =>
    setCreative({
      [slot]: { ...creative[slot], thumbnailUrl: next?.imageUrl ?? null, thumbnailPath: next?.imagePath ?? null },
    } as Partial<typeof creative>);

  // D3.4b — auto-extract a thumbnail from the local video file on upload (one
  // hook per video slot; applies only into an EMPTY thumbnail slot).
  const feedAutoThumb = useAutoThumbnail({
    accountId,
    slot: creative.feedAsset,
    apply: (img) => setAssetThumb('feedAsset', img),
  });
  const storyAutoThumb = useAutoThumbnail({
    accountId,
    slot: creative.storyAsset,
    apply: (img) => setAssetThumb('storyAsset', img),
  });

  // Asset-type switch (image ↔ video). Clears BOTH slots since Meta can't mix
  // image + video in one ad — confirmed first if anything is already uploaded.
  const applyAssetType = (next: 'image' | 'video') => {
    const empty = { type: null, url: null, path: null, thumbnailUrl: null, thumbnailPath: null } as const;
    onChange({
      ads: {
        ...ad,
        format: next === 'video' ? 'single_video' : 'single_image',
        creative: { ...creative, assetType: next, feedAsset: { ...empty }, storyAsset: { ...empty } },
      },
    });
  };
  const requestAssetType = (next: 'image' | 'video') => {
    if (next === assetType) return;
    const dirty = Boolean(creative.feedAsset.url || creative.storyAsset.url);
    if (dirty) setPendingAssetType(next);
    else applyAssetType(next);
  };

  function pickForm(f: LeadFormSummary) {
    onChange({ ads: { ...ad, destination: { type: 'instant_form', leadGenFormId: f.id, leadGenFormName: f.name } } });
    setFormOpen(false);
  }

  // Lead Forms inline create + duplicate. Create starts from a blank standard
  // form; duplicate fetches the source's FULL config and prefills the modal.
  function openCreateForm() {
    setLeadFormModalInitial(emptyCreateLeadFormInput());
    setLeadFormModalMode('create');
    setLeadFormModalOpen(true);
  }
  const fetchForDuplicate = useMutation({
    mutationFn: (formId: string) => getLeadForm(accountId, formId).then((r) => r.leadForm),
    onSuccess: (detail) => {
      setLeadFormModalInitial(leadFormDetailToCreateInput(detail));
      setLeadFormModalMode('duplicate');
      setLeadFormModalOpen(true);
    },
  });
  function startDuplicate(formId: string) {
    setDupOpen(false);
    fetchForDuplicate.mutate(formId);
  }
  // A newly created/duplicated form: select it for this ad AND refresh the list so
  // it appears in the picker (the server already busted its own cache).
  function onLeadFormCreated(form: { id: string; name: string }) {
    onChange({
      ads: { ...ad, destination: { type: 'instant_form', leadGenFormId: form.id, leadGenFormName: form.name } },
    });
    void qc.invalidateQueries({ queryKey: ['metaAdLeadForms', accountId] });
  }

  return (
    <section id="ad" className="space-y-8">
      <h2 className="text-lg font-semibold text-slate-900">Ad</h2>

      {/* D2.14 — optional ad name (falls back to the campaign name at publish). */}
      <div className="max-w-xl">
        <label htmlFor="ad-name" className="mb-1.5 block text-sm font-medium text-slate-700">
          Ad Name
        </label>
        <input
          id="ad-name"
          type="text"
          value={ad.name}
          onChange={(e) => onChange({ ads: { ...ad, name: e.target.value } })}
          placeholder={attachMode ? 'Name the new ad' : 'Defaults to campaign name'}
          className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-kyck-500 focus:outline-none focus:ring-1 focus:ring-kyck-500"
        />
        <p className="mt-1 text-xs text-slate-400">
          Optional. Set a specific name for this ad. Common pattern: "Mt Druitt EOFY - Headline V1".
        </p>
      </div>

      {/* Per-ad Active/Off — THIS (primary) ad's OWN status, independent of the ad
          set + campaign (a paused parent stops delivery but doesn't force this). */}
      <div className="max-w-xl">
        <PublishStatusToggle
          label="Ad state"
          value={ad.status}
          onChange={(status) => onChange({ ads: { ...ad, status } })}
        />
        {attachMode && (
          <p className="mt-1.5 text-xs text-slate-500" data-testid="attach-status-note">
            Active starts spending inside this ad set's existing budget straight away.
          </p>
        )}
      </div>

      {!exactReuse && (<>
      {/* ---------- Destination ---------- */}
      <div className="space-y-3">
        <div>
          <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Destination</h3>
          <p className="mt-1 text-xs text-slate-400">
            {attachMode
              ? attachMode.destination === 'unknown'
                ? 'We could not tell where this ad set sends people. Choose it, and make sure it matches the ad set in Meta.'
                : `${dest.type === 'instant_form' ? 'Instant form' : 'Website'} — set by the ad set you are adding to.`
              : dest.type === 'instant_form'
                ? 'Instant form — set by the Lead Gen goal.'
                : 'Website — set by the Traffic goal.'}
          </p>
          {attachMode?.destination === 'unknown' && (
            <div className="mt-2 inline-flex overflow-hidden rounded-md border border-slate-300" role="radiogroup" aria-label="Destination">
              {(['website', 'instant_form'] as const).map((t) => (
                <button
                  key={t}
                  type="button"
                  role="radio"
                  aria-checked={dest.type === t}
                  onClick={() =>
                    dest.type !== t &&
                    onChange({
                      ads: {
                        ...ad,
                        destination:
                          t === 'website'
                            ? { type: 'website', url: '' }
                            : { type: 'instant_form', leadGenFormId: '', leadGenFormName: '' },
                      },
                    })
                  }
                  className={['px-3 py-1.5 text-sm font-medium', dest.type === t ? 'bg-kyck-500 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'].join(' ')}
                >
                  {t === 'website' ? 'Website' : 'Instant form'}
                </button>
              ))}
            </div>
          )}
        </div>

        {dest.type === 'instant_form' ? (
          <div className="max-w-xl">
            <label className="mb-1.5 block text-sm font-medium text-slate-700">Lead form</label>
            <Popover open={formOpen} onOpenChange={setFormOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  disabled={!accountId}
                  className="flex w-full items-center justify-between rounded-md border border-slate-300 bg-white px-3 py-2 text-left text-sm hover:border-slate-400 disabled:bg-slate-50 disabled:text-slate-400"
                >
                  <span className={selectedForm || dest.leadGenFormName ? 'text-slate-900' : 'text-slate-400'}>
                    {selectedForm?.name || dest.leadGenFormName || (accountId ? 'Select a lead form…' : 'Pick an account first')}
                  </span>
                  <ChevronsUpDown className="h-4 w-4 text-slate-400" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                <Command>
                  <CommandInput placeholder="Search lead forms…" />
                  <CommandList>
                    <CommandEmpty>
                      {leadForms.isLoading
                        ? 'Loading…'
                        : 'No Lead Gen forms exist for this Page. Create one in Meta Ads Manager first.'}
                    </CommandEmpty>
                    <CommandGroup>
                      {(leadForms.data ?? []).map((f) => (
                        <CommandItem key={f.id} value={f.name} onSelect={() => pickForm(f)}>
                          <Check
                            className={['mr-2 h-4 w-4', f.id === (dest.type === 'instant_form' ? dest.leadGenFormId : '') ? 'opacity-100' : 'opacity-0'].join(' ')}
                          />
                          <span className="flex-1 truncate">{f.name}</span>
                          {f.leadsCount != null && (
                            <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-500">
                              {f.leadsCount} leads
                            </span>
                          )}
                          {f.questionTypes.length > 0 && (
                            <span className="ml-2 hidden text-xs text-slate-400 sm:inline">
                              {f.questionTypes.join(', ')}
                            </span>
                          )}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            {selectedForm?.followUpActionUrl && (
              <p className="mt-1.5 text-xs text-slate-500">Leads will be sent to: {selectedForm.followUpActionUrl}</p>
            )}

            {/* Inline create + duplicate — never leave for Meta's UI to make a form. */}
            <div className="mt-2 flex items-center gap-3">
              <button
                type="button"
                disabled={!accountReady}
                onClick={openCreateForm}
                className="inline-flex items-center gap-1 text-sm font-medium text-kyck-700 hover:underline disabled:text-slate-300 disabled:no-underline"
              >
                <Plus className="h-4 w-4" /> Create form
              </button>
              <Popover open={dupOpen} onOpenChange={setDupOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    disabled={!accountReady || fetchForDuplicate.isPending}
                    className="inline-flex items-center gap-1 text-sm font-medium text-kyck-700 hover:underline disabled:text-slate-300 disabled:no-underline"
                  >
                    <Copy className="h-4 w-4" />
                    {fetchForDuplicate.isPending ? 'Loading form…' : 'Duplicate existing'}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-80 p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Pick a form to duplicate…" />
                    <CommandList>
                      <CommandEmpty>
                        {leadForms.isLoading ? 'Loading…' : 'No forms to duplicate yet.'}
                      </CommandEmpty>
                      <CommandGroup>
                        {(leadForms.data ?? []).map((f) => (
                          <CommandItem key={f.id} value={f.name} onSelect={() => startDuplicate(f.id)}>
                            <span className="flex-1 truncate">{f.name}</span>
                            {f.questionTypes.length > 0 && (
                              <span className="ml-2 hidden text-xs text-slate-400 sm:inline">
                                {f.questionTypes.join(', ')}
                              </span>
                            )}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            {fetchForDuplicate.isError && (
              <p className="mt-1.5 text-xs text-red-600">Couldn't load that form. Please try again.</p>
            )}

            <LeadFormModal
              open={leadFormModalOpen}
              mode={leadFormModalMode}
              accountId={accountId}
              initial={leadFormModalInitial}
              onOpenChange={setLeadFormModalOpen}
              onCreated={onLeadFormCreated}
            />
          </div>
        ) : (
          <div className="max-w-xl">
            <label className="mb-1.5 block text-sm font-medium text-slate-700">Website URL</label>
            <input
              type="url"
              value={dest.url}
              placeholder="https://example.com/landing"
              onChange={(e) => onChange({ ads: { ...ad, destination: { type: 'website', url: e.target.value } } })}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-kyck-500 focus:outline-none focus:ring-1 focus:ring-kyck-500"
            />
            <p className="mt-1 text-xs text-slate-400">Where users land after clicking the ad.</p>
            {dest.url.trim() !== '' && !isHttpsUrl(dest.url) && (
              <p className="mt-1 text-xs text-amber-600">Enter a full https:// URL.</p>
            )}
          </div>
        )}
      </div>

      {!reuseMedia && (<>
      {/* ---------- Asset type selector (D3.4) ---------- */}
      <div className="space-y-2">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Creative</h3>
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-slate-700">Asset type:</span>
          <div className="inline-flex overflow-hidden rounded-md border border-slate-300" role="radiogroup" aria-label="Asset type">
            {(['image', 'video'] as const).map((t) => (
              <button
                key={t}
                type="button"
                role="radio"
                aria-checked={assetType === t}
                onClick={() => requestAssetType(t)}
                className={[
                  'px-3 py-1.5 text-sm font-medium transition-colors',
                  assetType === t ? 'bg-kyck-500 text-white' : 'bg-white text-slate-600 hover:bg-slate-50',
                ].join(' ')}
              >
                {t === 'image' ? 'Image' : 'Video'}
              </button>
            ))}
          </div>
        </div>
        <p className="text-xs text-slate-400">
          Both the Feed and Story assets share this type — Meta can’t mix image and video in one ad.
        </p>
      </div>

      {/* ---------- Feed asset ---------- */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
          Feed {assetType === 'video' ? 'video' : 'image'}
        </h3>
        <p className="text-xs text-slate-400">
          Used for all Feed-class placements (Facebook Feed, Instagram Feed, Marketplace, etc.). Required.{' '}
          {assetType === 'video' ? 'Recommended 4:5 or 1:1, MP4, up to 1GB.' : 'Recommended 4:5 or 1:1, up to 30MB.'}
        </p>
        <AssetUploader
          kind={assetType}
          value={slotToUploaded('feedAsset')}
          onChange={(asset) => setAsset('feedAsset', asset)}
          accountId={accountId}
          aspectGuidance="1:1 (1080×1080) or 4:5 (1080×1350)"
          onFileSelected={assetType === 'video' ? feedAutoThumb.onVideoFileSelected : undefined}
        />
        {assetType === 'video' && creative.feedAsset.url && (
          <VideoThumbnailField
            accountId={accountId}
            slot={creative.feedAsset}
            autoThumb={feedAutoThumb}
            required={false}
            onManualChange={(img) => setAssetThumb('feedAsset', img)}
            aspectGuidance="matches the Feed video ratio"
          />
        )}

        {/* ---------- Story asset (Phase 1 — inline expand below the Feed asset) ---------- */}
        {!showStory ? (
          <button
            type="button"
            onClick={() => setStoryOpen(true)}
            className="inline-flex items-center gap-1 rounded-md border border-dashed border-slate-300 px-3 py-1.5 text-sm font-medium text-kyck-700 hover:border-slate-400 hover:bg-slate-50"
          >
            <Plus className="h-4 w-4" /> Add Story-Specific Creative
          </button>
        ) : (
          <div className="space-y-3 rounded-md border border-slate-200 p-4">
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                Story {assetType === 'video' ? 'video' : 'image'}
              </h3>
              <button
                type="button"
                onClick={requestStoryRemove}
                className="text-xs font-medium text-slate-500 hover:text-slate-700 hover:underline"
              >
                Remove Story-Specific Creative
              </button>
            </div>
            <p className="text-xs text-slate-400">
              9:16 vertical asset for Story and Reels placements. If not provided, the Feed asset will be used
              everywhere.{' '}
              {assetType === 'video' ? 'Recommended 9:16, MP4, up to 1GB.' : 'Recommended 9:16, up to 30MB.'}
            </p>
            <AssetUploader
              kind={assetType}
              value={slotToUploaded('storyAsset')}
              onChange={(asset) => setAsset('storyAsset', asset)}
              accountId={accountId}
              aspectGuidance="9:16 (1080×1920)"
              onFileSelected={assetType === 'video' ? storyAutoThumb.onVideoFileSelected : undefined}
            />
            {assetType === 'video' && creative.storyAsset.url && (
              <VideoThumbnailField
                accountId={accountId}
                slot={creative.storyAsset}
                autoThumb={storyAutoThumb}
                required={false}
                onManualChange={(img) => setAssetThumb('storyAsset', img)}
                aspectGuidance="matches the Story video ratio"
              />
            )}
          </div>
        )}
      </div>

      </>)}
      {/* ---------- Copy (Phase 1 — Dynamic Creative toggle gates multi-input) ---------- */}
      <div className="space-y-5">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Ad copy</h3>

        {!attachMode && (
        <div className="max-w-xl space-y-2">
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm font-medium text-slate-700">Use Dynamic Creative (Multiple Text Options)</span>
            <button
              type="button"
              role="switch"
              aria-checked={dco}
              aria-disabled={dcoBlocked}
              aria-label="Use Dynamic Creative (Multiple Text Options)"
              title={dcoBlocked ? 'Not available with multiple ads' : undefined}
              onClick={requestDcoToggle}
              className={[
                'relative inline-flex h-5 w-9 items-center rounded-full transition-colors',
                dco ? 'bg-kyck-500' : 'bg-slate-300',
                dcoBlocked ? 'cursor-not-allowed opacity-50' : '',
              ].join(' ')}
            >
              <span
                className={['inline-block h-4 w-4 transform rounded-full bg-white transition-transform', dco ? 'translate-x-4' : 'translate-x-0.5'].join(' ')}
              />
            </button>
          </div>
          {dco && (
            <p className="rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-600">
              Meta requires Dynamic Creative ad sets for multi-text variations. Each ad set can only have one ad in
              this mode.
            </p>
          )}
        </div>
        )}

        <RepeatableCopyField
          label="Headlines"
          values={creative.headlines}
          onChange={(headlines) => setCreative({ headlines })}
          soft={40}
          min={1}
          max={dco ? 5 : 1}
          addLabel="Add headline"
        />

        <RepeatableCopyField
          label="Primary text"
          values={creative.primaryTexts}
          onChange={(primaryTexts) => setCreative({ primaryTexts })}
          soft={125}
          min={1}
          max={dco ? 5 : 1}
          multiline
          helper="The main body shown above the image."
          addLabel="Add primary text"
        />

        <RepeatableCopyField
          label="Descriptions"
          optional
          values={creative.descriptions}
          onChange={(descriptions) => setCreative({ descriptions })}
          soft={30}
          min={0}
          max={4}
          addLabel="Add description"
        />

        <div className="max-w-xs">
          <label htmlFor="ad-cta" className="mb-1.5 block text-sm font-medium text-slate-700">Call to action</label>
          <select
            id="ad-cta"
            value={creative.callToAction}
            onChange={(e) => setCreative({ callToAction: e.target.value as MetaCallToAction })}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-kyck-500 focus:outline-none focus:ring-1 focus:ring-kyck-500"
          >
            {CTA_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      </>)}
      {/* ---------- Asset-type switch confirmation (D3.4) ---------- */}
      <Dialog open={pendingAssetType !== null} onOpenChange={(o) => { if (!o) setPendingAssetType(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Switch to {pendingAssetType === 'video' ? 'video' : 'image'}?</DialogTitle>
            <DialogDescription>
              This will clear your uploaded {assetType === 'video' ? 'videos' : 'images'} (Feed and Story). You’ll
              need to upload {pendingAssetType === 'video' ? 'video' : 'image'} assets instead.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingAssetType(null)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (pendingAssetType) applyAssetType(pendingAssetType);
                setPendingAssetType(null);
              }}
            >
              Switch
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------- Dynamic Creative OFF confirmation (Phase 1) ---------- */}
      <Dialog open={pendingDcoOff} onOpenChange={(o) => { if (!o) setPendingDcoOff(false); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Switch to single ad creative?</DialogTitle>
            <DialogDescription>Extra headlines and primary texts will be removed.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingDcoOff(false)}>
              Cancel
            </Button>
            <Button onClick={applyDcoOff}>Switch</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------- Story-asset removal confirmation (Phase 1) ---------- */}
      <Dialog open={confirmStoryRemove} onOpenChange={(o) => { if (!o) setConfirmStoryRemove(false); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Remove Story-specific creative?</DialogTitle>
            <DialogDescription>
              This won’t delete uploaded files, but they won’t be sent with the next publish.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmStoryRemove(false)}>
              Cancel
            </Button>
            <Button onClick={applyStoryRemove}>Remove</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Instagram delivery identity + the Page/IG connection summary now live in
          ONE place — AdSetSection's "Meta Account Details" — sourced from the
          account's IG (not the Page's native link). The IG id flows to publish via
          ad.instagramAccountId (seeded on account-pick), unchanged. */}

      {/* ---------- Additional ads (Phase 2 — sequential attach) ---------- */}
      {!attachMode && (
      <div className="space-y-4">
        {additionalAds.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Additional ads</h3>
            <p className="mt-1 text-xs text-slate-400">
              Published into the same ad set — budget, audience and schedule are shared. Each ad has its own
              image or video{isLeadGen ? '' : ', link'} and optional copy overrides.
            </p>
          </div>
        )}
        {additionalAds.map((entry, i) => (
          <AdditionalAdCard
            key={entry.id}
            ordinal={i + 2}
            value={entry}
            shared={sharedCopy}
            isLeadGen={isLeadGen}
            accountId={accountId}
            onChange={(patch) =>
              setAdditionalAds(additionalAds.map((a) => (a.id === entry.id ? { ...a, ...patch } : a)))
            }
            onRemove={() => setAdditionalAds(additionalAds.filter((a) => a.id !== entry.id))}
          />
        ))}
        {additionalAds.length < 9 && (
          <button
            type="button"
            onClick={requestAddAd}
            className="inline-flex items-center gap-1 rounded-md border border-dashed border-slate-300 px-3 py-1.5 text-sm font-medium text-kyck-700 hover:border-slate-400 hover:bg-slate-50"
          >
            <Plus className="h-4 w-4" /> Add Another Ad
          </button>
        )}
      </div>
      )}

      {/* ---------- Shared tracking (UTM + Pixel) ---------- */}
      {/* One UTM set + one pixel for the whole ad set (consistent across every ad),
          so they render ONCE here — after all the ads, before the publish step —
          rather than interleaved between the ads above. */}

      {/* ---------- UTM tracking ---------- */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">UTM tracking</h3>
        <p className="text-xs text-slate-400">
          Tracking parameters appended to the destination URL. Values pass through literally, so you can use Meta
          placeholders like <code className="text-slate-500">{'{{campaign.id}}'}</code> or{' '}
          <code className="text-slate-500">{'{{ad.id}}'}</code>.
        </p>
        <div className="grid max-w-xl grid-cols-2 gap-3">
          {(
            [
              ['source', 'Source (utm_source)'],
              ['medium', 'Medium (utm_medium)'],
              ['campaign', 'Campaign (utm_campaign)'],
              ['content', 'Content (utm_content)'],
              ['term', 'Term (utm_term)'],
            ] as [keyof UtmParams, string][]
          ).map(([key, lbl]) => (
            <div key={key}>
              <label htmlFor={`utm-${key}`} className="mb-1 block text-xs font-medium text-slate-600">
                {lbl}
              </label>
              <input
                id={`utm-${key}`}
                type="text"
                value={ad.utmParams[key]}
                onChange={(e) => setUtm({ [key]: e.target.value } as Partial<UtmParams>)}
                className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:border-kyck-500 focus:outline-none focus:ring-1 focus:ring-kyck-500"
              />
            </div>
          ))}
        </div>
        {utmPreview && (
          <p className="max-w-xl break-all rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-600">
            <span className="font-medium text-slate-500">Preview: </span>
            {utmPreview}
          </p>
        )}
      </div>

      {/* ---------- Pixel ---------- */}
      {!attachMode && (
      <div className="max-w-xl space-y-2">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Conversion pixel</h3>
        <Popover open={pixelOpen} onOpenChange={setPixelOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              disabled={!accountReady}
              className="flex w-full items-center justify-between rounded-md border border-slate-300 bg-white px-3 py-2 text-left text-sm hover:border-slate-400 disabled:bg-slate-50 disabled:text-slate-400"
            >
              <span className={selectedPixel || ad.pixelId ? 'text-slate-900' : 'text-slate-400'}>
                {selectedPixel?.name || ad.pixelId || (accountReady ? 'Select a pixel…' : 'Pick an account first')}
              </span>
              <ChevronsUpDown className="h-4 w-4 text-slate-400" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
            <Command>
              <CommandInput placeholder="Search pixels…" />
              <CommandList>
                <CommandEmpty>
                  {pixels.isLoading
                    ? 'Loading…'
                    : 'No pixels found for this ad account. Create one in Meta Events Manager first.'}
                </CommandEmpty>
                <CommandGroup>
                  {(pixels.data ?? []).map((p) => (
                    <CommandItem key={p.id} value={p.name} onSelect={() => pickPixel(p)}>
                      <Check className={['mr-2 h-4 w-4', p.id === ad.pixelId ? 'opacity-100' : 'opacity-0'].join(' ')} />
                      <span className="flex-1 truncate">{p.name}</span>
                      {p.status && (
                        <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-xs capitalize text-slate-500">
                          {p.status.toLowerCase()}
                        </span>
                      )}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        <p className="text-xs text-slate-400">Used for conversion tracking and audience building.</p>
      </div>
      )}

      {/* ---------- DCO-on → add-ad confirmation (Phase 2) ---------- */}
      <Dialog open={confirmAddWithDco} onOpenChange={(o) => { if (!o) setConfirmAddWithDco(false); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Switch to standard ads?</DialogTitle>
            <DialogDescription>
              Multiple ads can’t be combined with Dynamic Creative (Meta allows one ad per Dynamic Creative ad
              set). Switch to standard ads? Extra headlines and primary texts will be trimmed to the first.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmAddWithDco(false)}>
              Cancel
            </Button>
            <Button onClick={applyAddWithDco}>Switch</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------- multi-ad → DCO confirmation (Phase 2) ---------- */}
      <Dialog open={confirmRemoveAdsForDco} onOpenChange={(o) => { if (!o) setConfirmRemoveAdsForDco(false); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Remove the additional ads?</DialogTitle>
            <DialogDescription>
              Dynamic Creative requires a single ad. Remove the additional ads?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmRemoveAdsForDco(false)}>
              Cancel
            </Button>
            <Button onClick={applyRemoveAdsForDco}>Remove ads</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
